import {
  PROVINCE_TERRITORY_NAMES,
  STATE_NAMES,
} from "encamp-shared/src/constants/states";
import { Country } from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { sortBy } from "lodash-es";
import { useEffect, useMemo } from "react";
import { UseControllerProps } from "react-hook-form";
import { DisplayOnlyField } from "./Forms/DisplayOnlyField";
import { FormSelect, SelectItem } from "./Forms/FormSelect";

type Props = {
  disabled?: boolean;
  countryFieldName?: string;
} & UseControllerProps;

export function StatePicker(props: Props) {
  const { watch, setValue, getFieldState, metadata } =
    useValidatingFormContext();
  const country = watch(props.countryFieldName || "country");
  const state = watch(props.name);

  const isCountryValid = useMemo(() => {
    return (
      !country ||
      (country === Country.Usa &&
        STATE_NAMES[state as keyof typeof STATE_NAMES])
    );
  }, [country, state]);

  useEffect(() => {
    // reset the state if the country changed and the current state is not valid
    if (
      props.countryFieldName &&
      getFieldState(props.countryFieldName).isDirty &&
      !isCountryValid
    ) {
      setValue(props.name, null);
    }
  }, [
    country,
    setValue,
    getFieldState,
    props.name,
    props.countryFieldName,
    isCountryValid,
  ]);

  const selectItems: SelectItem[] = useMemo(
    () =>
      sortBy(
        Object.entries(
          country === Country.Canada ? PROVINCE_TERRITORY_NAMES : STATE_NAMES
        ),
        1
      ).map(([abbr, name]) => ({
        display: name,
        value: abbr,
      })),
    [country]
  );

  const label = useMemo(() => {
    return country === Country.Canada ? "Province/Territory" : "State";
  }, [country]);

  if (metadata?.isDisplayOnly) {
    const text = selectItems.find((item) => item.value === state)?.display;
    return <DisplayOnlyField text={text ?? ""} label={label} />;
  }

  return <FormSelect {...props} label={label} selectItems={selectItems} />;
}
